import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setCurrentLanguage, selectCurrentLanguage, selectI18nEnabled, selectSupportedLanguages } from '../store/i18nSlice';
import { SUPPORTED_LANGUAGES } from '../utils/i18nConfig';
import { MdLanguage } from 'react-icons/md';

/**
 * LanguageSwitcher Component
 * 
 * Displays a language selector that allows users to switch between available languages.
 * Only shown if i18n is enabled for the tenant.
 * 
 * Features:
 * - Shows only supported languages for the tenant
 * - Saves language preference to localStorage
 * - Triggers UI re-render on language change
 */
export function LanguageSwitcher() {
  const dispatch = useDispatch();
  const currentLanguage = useSelector(selectCurrentLanguage);
  const i18nEnabled = useSelector(selectI18nEnabled);
  const supportedLanguages = useSelector(selectSupportedLanguages);
  const [isOpen, setIsOpen] = useState(false);

  // Don't show switcher if i18n is disabled
  if (!i18nEnabled || supportedLanguages.length <= 1) {
    return null;
  }

  const handleLanguageChange = (languageCode) => {
    dispatch(setCurrentLanguage(languageCode));
    setIsOpen(false);
    // Force re-render by refreshing the page (optional, depending on your i18n setup)
    // window.location.reload();
  };

  const currentLangData = SUPPORTED_LANGUAGES[currentLanguage] || SUPPORTED_LANGUAGES['en'];

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
        title="Change language"
      >
        <MdLanguage className="text-lg" />
        <span className="text-sm font-medium hidden sm:inline">
          {currentLangData.nativeName}
        </span>
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg z-50 border border-gray-200 dark:border-gray-700">
          <div className="p-2">
            {supportedLanguages.map((langCode) => {
              const langData = SUPPORTED_LANGUAGES[langCode] || {};
              const isActive = currentLanguage === langCode;

              return (
                <button
                  key={langCode}
                  onClick={() => handleLanguageChange(langCode)}
                  className={`w-full text-left px-4 py-2 rounded-lg transition-colors ${
                    isActive
                      ? 'bg-blue-100 dark:bg-blue-900 text-blue-900 dark:text-blue-100 font-semibold'
                      : 'hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`}
                >
                  <span className="mr-2">{langData.flag}</span>
                  <span>{langData.nativeName}</span>
                  {isActive && <span className="ml-2">✓</span>}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export default LanguageSwitcher;
